import React, { useMemo, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import {
  WEEK_LIMIT_COUNT,
  ALLOCATION_TYPE_BOOKING,
  ALLOCATION_TYPE_CURRENT,
  PERSISTING_CANDIDATE_BOOKING_STATUSES,
} from 'constants/bookingAllocation';
import { dashboardColors } from 'constants/chartColors';
import { EMPTY_ALLOCATION_CHART_MESSAGE } from 'constants/dashboard';
import { ALLOCATION_PILL_TAB_OPTIONS } from 'constants/appConstants';

import { useFetchUserBookingAllocationQuery, useFetchUserCurrentAllocationsQuery } from 'hooks/query/useUserQueries';

import { getRandomColor, pluralize, startCase } from 'utils/string';
import { isDateToday, isDateInFuture, getFormattedDate, getWeeksLaterDate } from 'utils/date';
import type { AllocationItem } from 'interface/allocation';

import PillTab from 'components/common/pillTab';
import CommonChart from 'components/common/piechart/CommonChart';
import BookingAllocation, { BookedAllocation } from './components/BookingAllocation';

type Area = {
  name?: string;
};

type Designation = {
  area?: Area;
};

type ProjectAllocation = AllocationItem;

type BookingStatus = NonNullable<BookedAllocation['bookingStatus']> & {
  statusUpdatedAt?: string;
};

type BookingAllocationData = BookedAllocation & {
  bookingStatus: BookingStatus;
};

type ChartDatum = {
  key: number;
  label: string;
  value: number;
  color: string;
};

type AllocationAndBookingProps = {
  userId: number | string;
  designation?: Designation;
};

function AllocationAndBooking({ userId }: AllocationAndBookingProps) {
  const {
    data: currentAllocationsData,
    isLoading: currentAllocationLoading,
    isError: isCurrentAllocationError,
  } = useFetchUserCurrentAllocationsQuery<ProjectAllocation[]>(userId, Boolean(userId));

  const { data: bookingAllocationData, isLoading: bookingAllocationLoading } =
    useFetchUserBookingAllocationQuery<BookingAllocationData | null>(userId, Boolean(userId));

  const projectAllocations = useMemo(() => {
    if (!currentAllocationsData) {
      return [];
    }

    return [...currentAllocationsData]
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .filter((allocation) =>
        allocation.endDate ? isDateToday(allocation.endDate) || isDateInFuture(allocation.endDate) : true
      );
  }, [currentAllocationsData]);

  const isError = isCurrentAllocationError;

  const colors = useMemo(() => {
    if (projectAllocations.length <= dashboardColors.length) {
      return dashboardColors;
    }

    const totalColorsToBeAdded = projectAllocations.length - dashboardColors.length;
    const colorsToBeAdded = Array.from({ length: totalColorsToBeAdded + 1 }, getRandomColor);

    return [...dashboardColors, ...colorsToBeAdded];
  }, [projectAllocations.length]);

  const bookedAllocation = useMemo(() => {
    if (!bookingAllocationData) {
      return null;
    }

    const limitDate = getWeeksLaterDate(
      bookingAllocationData.bookingStatus.statusUpdatedAt || bookingAllocationData.targetDate,
      WEEK_LIMIT_COUNT
    );

    const includesPersistingStatus = PERSISTING_CANDIDATE_BOOKING_STATUSES.includes(
      bookingAllocationData.bookingStatus.name
    );

    if (includesPersistingStatus && getFormattedDate() > limitDate) {
      return null;
    }

    return bookingAllocationData;
  }, [bookingAllocationData]);

  const allocationSourceKey = `${currentAllocationLoading}:${bookingAllocationLoading}:${projectAllocations.length}:${
    bookedAllocation ? 'booking' : 'current'
  }`;
  const defaultAllocation =
    !currentAllocationLoading && !bookingAllocationLoading && !projectAllocations.length && bookedAllocation
      ? ALLOCATION_TYPE_BOOKING
      : ALLOCATION_TYPE_CURRENT;
  const [allocationDraft, setAllocationDraft] = useState(() => ({
    sourceKey: allocationSourceKey,
    value: defaultAllocation,
  }));
  const allocation = allocationDraft.sourceKey === allocationSourceKey ? allocationDraft.value : defaultAllocation;
  const chartData = useMemo<ChartDatum[]>(() => {
    if (!projectAllocations.length) {
      return [];
    }

    return projectAllocations.map(({ allocation, project, allocationType, area }, index) => {
      return {
        key: index,
        label: project.name || area.name || startCase(allocationType),
        value: allocation,
        color: colors[index],
      };
    });
  }, [colors, projectAllocations]);

  const onToggleChange = (newValue: string | number) => {
    setAllocationDraft({ sourceKey: allocationSourceKey, value: String(newValue) });
  };

  const isLoading = currentAllocationLoading || bookingAllocationLoading;

  return (
    <>
      <div className="dashboard__title dashboard__title--allocation">
        <div className="dashboard__title--allocation-text">
          <div>{pluralize('My Allocation', projectAllocations.length)}</div>
        </div>

        {bookedAllocation && (
          <div className="dashboard__toggle-tab">
            <PillTab
              tabs={ALLOCATION_PILL_TAB_OPTIONS}
              onTabClick={(value) => onToggleChange(value)}
              isActive={(value) => value === allocation}
            />
          </div>
        )}
      </div>
      {isLoading && (
        <SkeletonTheme baseColor="#fdfdfd" highlightColor="#f0f3f5">
          <Skeleton className="allocation-card-loader wp-100" height={196} />
        </SkeletonTheme>
      )}
      {!isLoading && allocation === ALLOCATION_TYPE_CURRENT && (
        <div className="dashboard__content flex-1 d-flex align-items-center justify-content-center">
          {isError && (
            <div className="container mt-25">
              <div className="allocation__empty">
                <p> Sorry, couldn&apos;t load the allocation list. Please try again!.</p>
              </div>
            </div>
          )}

          {!isError && (
            <div className="allocation__content">
              {chartData.length ? (
                <CommonChart
                  className="allocation__chart"
                  chartData={chartData}
                  appendLabel={{ singular: 'hr/week', plural: 'hrs/week' }}
                  colors={colors}
                  projectAllocations={projectAllocations}
                />
              ) : (
                <div className="allocation__empty">
                  <p>{EMPTY_ALLOCATION_CHART_MESSAGE}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isLoading && allocation !== ALLOCATION_TYPE_CURRENT && (
        <div>
          <div className="booking-allocation custom-scroll-bar px-16 ptpx-12 pbpx-12">
            <BookingAllocation bookedAllocation={bookedAllocation} />
          </div>
        </div>
      )}
    </>
  );
}

export default AllocationAndBooking;
